<template>
  <div v-if="show" class="modal-overlay" @click.self="handleCancel">
    <div class="modal-container">
      <div class="modal-header" v-if="title">
        <h3>{{ title }}</h3>
      </div>
      <div class="modal-body">
        <p v-if="message">{{ message }}</p>
        <ul v-if="type === 'list'" class="name-list">
          <li v-for="(entry, index) in names" :key="index">{{ entry }}</li>
          <li v-if="!names.length" class="empty">Nobody has voted yet.</li>
        </ul>
        <input
          v-if="type === 'edit'"
          v-model="inputValue"
          type="text"
          class="modal-input"
          @keyup.enter="handleConfirm"
          @keyup.esc="handleCancel"
          ref="inputRef"
        />
        <slot></slot>
      </div>
      <div class="modal-footer">
        <button v-if="type !== 'list'" @click="handleCancel" class="modal-button cancel-button">
          Cancel
        </button>
        <button
          @click="handleConfirm"
          class="modal-button confirm-button"
          :disabled="confirmDisabled"
        >
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ModalDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'confirm', // 'confirm' or 'edit'
      validator: (value) => ['confirm', 'edit', 'list'].includes(value)
    },
    names: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    initialValue: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: 'Confirm'
    },
    confirmDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputValue: ''
    };
  },
  watch: {
    show(newVal) {
      if (newVal && this.type === 'edit') {
        this.inputValue = this.initialValue;
        this.$nextTick(() => {
          if (this.$refs.inputRef) {
            this.$refs.inputRef.focus();
            this.$refs.inputRef.select();
          }
        });
      }
    },
    initialValue(newVal) {
      if (this.type === 'edit') {
        this.inputValue = newVal;
      }
    }
  },
  methods: {
    handleConfirm() {
      if (this.confirmDisabled) {
        return;
      }
      if (this.type === 'edit') {
        if (this.inputValue.trim() !== '') {
          this.$emit('confirm', this.inputValue.trim());
        }
      } else {
        this.$emit('confirm');
      }
    },
    handleCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.3);
  width: min(400px, calc(100vw - 32px));
  min-width: 0;
  max-width: calc(100vw - 32px);
  max-height: 90vh;
  overflow: auto;
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 20px 20px 10px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.modal-body {
  padding: 20px;
}

.modal-body p {
  margin: 0 0 15px 0;
  color: #333;
  line-height: 1.5;
}

.modal-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.modal-input:focus {
  outline: none;
  border-color: #3498db;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button {
  background-color: #e0e0e0;
  color: #333;
}

.cancel-button:hover {
  background-color: #d0d0d0;
}

.confirm-button {
  background-color: #3498db;
  color: #fff;
}

.confirm-button:hover {
  background-color: #2980b9;
}

.confirm-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.name-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.name-list li {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  color: #333;
}

.name-list li:last-child {
  border-bottom: none;
}

.name-list .empty {
  color: #888;
  font-style: italic;
}

@media (max-width: 600px) {
  .modal-footer {
    flex-wrap: wrap;
  }

  .modal-button {
    min-height: 44px;
  }
}
</style>

